import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Loader2, CheckCircle, XCircle, RotateCcw } from 'lucide-react';

export default function MicTester({ isDarkMode, setIsReady }) {
  const streamRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const sourceRef = useRef(null) // rebuilt per-stream; the AudioContext is reused
  const rafRef = useRef(null)
  const barRef = useRef(null) // written to directly (not React state) — updates every frame
  const [streamStatus, setStreamStatus] = useState('idle') // idle | requesting | granted | denied | error
  const [isMuted, setIsMuted] = useState(false) // toggles the track's .enabled without dropping the stream

  // Create the AudioContext + AnalyserNode once and reuse them: constructing an AudioContext is
  // ~1s synchronous and was blocking React's paint on every grant/retry.
  const ensureAudioContext = () => {
    if (!audioContextRef.current) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      audioContextRef.current = new AudioContextClass()
      analyserRef.current = audioContextRef.current.createAnalyser()
    }
    return audioContextRef.current
  }

  // Stops the polling loop + disconnects the source. Does NOT close the AudioContext (reused; closed on unmount).
  const stopLevelMeter = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect()
      sourceRef.current = null
    }
    if (barRef.current) {
      barRef.current.style.width = '0%'
    }
    setIsMuted(false)
  }

  const toggleMute = () => {
    const audioTrack = streamRef.current?.getAudioTracks()[0]
    if (!audioTrack) return
    audioTrack.enabled = !audioTrack.enabled
    setIsMuted(!audioTrack.enabled)
  }

  const startLevelMeter = (stream) => {
    const audioContext = ensureAudioContext() // no-op if already created
    const analyser = analyserRef.current
    const source = audioContext.createMediaStreamSource(stream)
    source.connect(analyser) // do NOT connect to destination — that plays the mic back to the speakers (feedback)
    sourceRef.current = source

    const dataArray = new Uint8Array(analyser.frequencyBinCount)

    const tick = () => {
      analyser.getByteTimeDomainData(dataArray)
      let sum = 0
      for (let i = 0; i < dataArray.length; i++) {
        sum += Math.abs(dataArray[i] - 128)
      }
      const level = sum / dataArray.length
      // 40 ≈ "loud speech" on the 0-128 scale, so the bar uses its full range. Written straight to
      // the DOM (not setState) — this runs every frame and setState here flooded the render queue.
      if (barRef.current) {
        barRef.current.style.width = `${Math.min(100, (level / 40) * 100)}%`
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    tick()
  }

  // Shared by the button click and the auto-restore-on-mount flow. muteAfterGrant=true (auto-restore)
  // brings the mic back granted but silenced.
  const acquireStream = async (muteAfterGrant = false) => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    stopLevelMeter();
    ensureAudioContext(); // pre-warm in parallel with the permission prompt
    try {
      setStreamStatus('requesting')
      console.log("Requesting for mic access...")
      const userMedia = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = userMedia
      console.log("Access granted")
      setIsReady(true)
      setStreamStatus('granted')
      startLevelMeter(userMedia)
      if (muteAfterGrant) {
        toggleMute()
      }
    } catch (e){
      if (e.name === "NotAllowedError"){
        setStreamStatus('denied')
        console.error("User block the mic", e)
      } else if (e.name === "NotFoundError") {
        setStreamStatus('error')
        console.error("Mic not found", e)
      } else if (e.name === "NotReadableError") {
        setStreamStatus('error')
        console.error("Mic exists but system cannot access", e)
      } else {
        setStreamStatus('error')
        console.error("Error getting user mic test", e)
      }
    }
  }

  const handleGetUserMedia = () => {
    if (streamStatus === 'requesting') return; // block re-entrant calls
    acquireStream(false)
  }

  // On mount: if mic permission is already granted (user navigated away and back), silently
  // re-acquire the stream muted instead of making them click again. The Permissions API isn't
  // universal (Safari) — on throw/unsupported, just stay idle.
  useEffect(() => {
    let cancelled = false
    const checkExistingPermission = async () => {
      try {
        const status = await navigator.permissions.query({ name: 'microphone' })
        if (!cancelled && status.state === 'granted') {
          acquireStream(true)
        }
      } catch (e) {
        console.log("Permissions API query for microphone not supported, skipping auto-restore", e)
      }
    }
    checkExistingPermission()
    return () => {
      cancelled = true
    }
  }, [])

  // On unmount: release the mic, audio graph, and the AudioContext (which stopLevelMeter leaves
  // open). Doesn't touch the browser's permission decision.
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect()
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [])

  const handleMicButtonClick = () => {
    if (streamStatus === 'idle' || streamStatus === 'denied' || streamStatus === 'error') {
      handleGetUserMedia();
    } else if (streamStatus === 'granted') {
      toggleMute();
    }
  };

  const bigButtonClass = (() => {
    if (streamStatus === 'requesting') {
      return `${isDarkMode ? 'bg-slate-700' : 'bg-slate-300'} opacity-50 cursor-not-allowed`;
    }
    if (streamStatus === 'granted') {
      return isMuted
        ? (isDarkMode ? 'bg-red-700 hover:bg-red-600' : 'bg-red-600 hover:bg-red-500')
        : 'bg-blue-600 hover:bg-blue-500';
    }
    return isDarkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-300 hover:bg-slate-400';
  })();

  const neutralIconColor = isDarkMode ? 'text-slate-300' : 'text-slate-600';

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        onClick={handleMicButtonClick}
        type="button"
        disabled={streamStatus === 'requesting'}
        aria-label={
          streamStatus === 'granted'
            ? (isMuted ? 'Unmute microphone' : 'Mute microphone')
            : (streamStatus === 'denied' || streamStatus === 'error')
              ? 'Retry microphone access'
              : 'Request microphone access'
        }
        className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors ${bigButtonClass}`}
      >
        {streamStatus === 'idle' && <Mic size={32} className={neutralIconColor} />}
        {streamStatus === 'requesting' && <Loader2 size={32} className={`${neutralIconColor} animate-spin`} />}
        {streamStatus === 'granted' && !isMuted && <Mic size={32} className="text-white" />}
        {streamStatus === 'granted' && isMuted && <MicOff size={32} className="text-white" />}
        {(streamStatus === 'denied' || streamStatus === 'error') && <RotateCcw size={32} className={neutralIconColor} />}
      </button>

      {streamStatus === 'idle' && <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Press to Allow Mic Access</p>}
      {streamStatus === 'requesting' && <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Waiting for Permission...</p>}

      {streamStatus === 'granted' && (
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${
          isDarkMode
            ? 'border-primary-400/40 bg-primary-400/10 text-primary-400'
            : 'border-primary-500/50 bg-primary-500/10 text-primary-600'
        }`}>
          <CheckCircle size={13} />
          Access Granted
        </span>
      )}

      {(streamStatus === 'denied' || streamStatus === 'error') && (
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${
          isDarkMode
            ? 'border-red-400/40 bg-red-400/10 text-red-400'
            : 'border-red-500/50 bg-red-500/10 text-red-600'
        }`}>
          <XCircle size={13} />
          {streamStatus === 'denied' ? 'Access Denied' : 'System Error'}
        </span>
      )}

      {streamStatus === 'granted' && (
        <div className={`w-48 h-3 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
          <div
            ref={barRef}
            className={`h-full rounded-full ${isDarkMode ? 'bg-blue-400' : 'bg-blue-500'}`}
            style={{ width: '0%', transition: 'width 50ms linear' }}
          />
        </div>
      )}
    </div>
  )
}
